import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { isInitialRenderWithResultsAtom } from '@/pages/Search/SearchPage/atoms/searchPageAtoms';
import { useAppSelector } from '@/redux/hooks';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { getSecondarySearchSuggestions } from '@/redux/modules/searchSuggestions/searchSuggestions.selectors';
import { useTrackAuctionHouseSuggestionClick } from '@/utils/amplitude/analytics';
import { AmplitudeExperiments, AmplitudeVariants } from '@/utils/amplitude/experiments/AmplitudeExperiments';
import { useAmplitudeExperiments } from '@/utils/amplitude/experiments/utils/useAmplitudeExperiments';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { safelyEscapeText } from '@/utils/sanitize';
import { getSellerUrl } from '@/utils/urls';

type Props = {
    isLoadingSearchResults: boolean;
};

export const AuctionHouseSuggestionLink = ({ isLoadingSearchResults }: Props) => {
    const isInitialRenderWithResults = useAtomValue(isInitialRenderWithResultsAtom);

    const { houses } = useAppSelector(getSecondarySearchSuggestions);

    const formatMessage = useAppFormatMessage();
    const trackAuctionHouseSuggestionClick = useTrackAuctionHouseSuggestionClick();

    // Experiment: sear-2212-did-you-mean-zero-result-searches. Exposure fires from the SearchPage
    // mount; read the resolved variant here instead of tracking it again.
    const { variants } = useAmplitudeExperiments();
    const isDidYouMeanTreatment =
        variants[AmplitudeExperiments.DidYouMeanZeroResultSearches]?.value ===
        AmplitudeVariants.DidYouMeanZeroResultSearches.Treatment;

    const searchSliceState = useAppSelector(getSearchUiState);
    const filterApplied = searchSliceState.queryModifications?.queryRewrite?.filterApplied;
    const shouldHideAuctionHouseSuggestionLink =
        isDidYouMeanTreatment || (filterApplied?.field === 'AuctionHouse' && filterApplied?.values?.length === 1);

    const firstHouse = useMemo(() => {
        if (houses.length > 0) {
            return houses[0];
        }
        return null;
    }, [houses]);

    if ((!firstHouse && !isInitialRenderWithResults) || shouldHideAuctionHouseSuggestionLink) {
        return null;
    }

    return (
        <div
            className={cn('mb-sm', {
                hidden: isInitialRenderWithResults || (!firstHouse && isLoadingSearchResults),
            })}
            data-testid="auctionHouseSuggestionLinkContainer"
        >
            <BodyPrimary>
                {formatMessage(['searchResults', 'lookingFor'], {
                    values: {
                        suggestion: firstHouse ? (
                            <Link
                                aria-label={firstHouse.value}
                                href={getSellerUrl(firstHouse.houseId, firstHouse.value)}
                                key="house-link"
                                onClick={() => trackAuctionHouseSuggestionClick({ houseId: firstHouse.houseId })}
                            >
                                {safelyEscapeText(firstHouse.value).trim()}
                            </Link>
                        ) : (
                            <span key="house-suggestion" />
                        ),
                    },
                })}
            </BodyPrimary>
        </div>
    );
};
